import { IonIcon, IonSpinner } from '@ionic/react';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { lockClosed } from 'ionicons/icons';
import React from 'react';

import { useAccount } from '@/hooks/useAccount';
import { usePollingUser } from '@/hooks/usePollingUser';
import { amplitudeModule } from '@/modules/amplitude';
import { apiV1GetMeAuthRequest } from '@/modules/api';
import { AxonModule } from '@/modules/axon';
import { webSubscriptionsModule } from '@/modules/subscriptions';
import { webCampaign } from '@/modules/webCampaign';
import {
  StripeV2CoinsPrice,
  StripeV2SubscriptionPrice,
} from '@/services/stripe/dto';
import { createAxonPayloadForPlan } from '@/utils/axonUtils';
import { canLogEvents } from '@/utils/canLogEvents';
import { pollUserSubscriptionUntilAttached } from '@/utils/pollUserSubscriptionUntilAttached';
import { savePurchasedPlan } from '@/utils/purchasedPlanStorage';
import { redirectToWebapp } from '@/utils/redirectToWebapp';
import { sendAnalitycsAfterSubscription } from '@/utils/sendAnalitycsAfterSubscription';

import {
  ButtonStyles,
  MessageStyles,
  StripeCardFormStyles,
} from './StripeCardForm.styles';

interface StripeCardFormProps {
  clientSecret: string;
  billing_portal_link: string;
  plan: StripeV2SubscriptionPrice;
  disabled: boolean;
  theme?: 'dark-violet' | 'domination' | 'dark-forest';
  buttonText?: string;
  selectedCoinPlan?: StripeV2CoinsPrice | null;
}

export const StripeCardForm: React.FC<StripeCardFormProps> = ({
  clientSecret,
  billing_portal_link,
  plan,
  disabled,
  theme,
  buttonText = 'Pay',
  selectedCoinPlan,
}) => {
  const { user } = useAccount();
  const { startPolling } = usePollingUser();
  const userId = user?.id || null;

  const [stripePurchase, setStripePurchase] = React.useState({
    error: '',
    success: false,
    isSubmitting: false,
  });

  const elements = useElements();
  const stripe = useStripe();

  const disablePurchase = !elements || !stripe;

  const handlePaymentElementFocus = React.useCallback(() => {
    if (!AxonModule.hasSentBeginCheckout()) {
      AxonModule.setBeginCheckoutSent();
      const payload = createAxonPayloadForPlan(plan);
      AxonModule.pushEvent(
        'begin_checkout',
        'axon_begin_checkout',
        payload,
        userId,
      );
    }
  }, [plan, userId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (disablePurchase) {
      return;
    }

    const paymentElement = elements.getElement(PaymentElement);
    if (!paymentElement) {
      setStripePurchase({
        error: 'Payment form is not ready. Please try again in a moment.',
        success: false,
        isSubmitting: false,
      });
      return;
    }

    setStripePurchase((prev) => ({ ...prev, isSubmitting: true }));

    await elements.submit();

    const { paymentMethod } = await stripe.createPaymentMethod({
      elements,
    });

    if (paymentMethod) {
      // Check if the card is prepaid (only if campaign is configured to reject them)
      if (
        webCampaign.shouldRejectPrepaidCards() &&
        paymentMethod.card &&
        paymentMethod.card.funding === 'prepaid'
      ) {
        const rejectionMessage = webCampaign.getPrepaidCardRejectionMessage();

        // Only reject if there's a specific rejection message
        if (rejectionMessage) {
          // Log prepaid card declined event to Amplitude
          if (canLogEvents()) {
            amplitudeModule.logEvent('prepaid_card_declined', {
              campaign_id: webCampaign.getKey(),
            });
          }

          setStripePurchase({
            error: rejectionMessage,
            success: false,
            isSubmitting: false,
          });
          return;
        }
        // If no rejection message, continue with payment process
      }
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      clientSecret,
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (stripeError) {
      let message = stripeError.message ?? 'Unknown error';
      const isApplePayError = message.includes('Unable to show Apple Pay');

      if (isApplePayError) {
        message = 'Something went wrong with Apple Pay.';
      }

      setStripePurchase({
        error: message,
        success: false,
        isSubmitting: false,
      });
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      if (webCampaign.setup.redirectToWebappAfter === 'subscription') {
        setStripePurchase({
          error: '',
          success: true,
          isSubmitting: false,
        });

        try {
          const response = await pollUserSubscriptionUntilAttached();

          if (response?.subscription) {
            if (canLogEvents()) {
              sendAnalitycsAfterSubscription(
                plan,
                selectedCoinPlan,
                paymentIntent.id,
              );
            }

            const auth = await apiV1GetMeAuthRequest();
            redirectToWebapp(auth.token);
            return;
          }
        } catch (error) {
          console.error('Failed to poll user subscription: ', error);
        }
      }

      startPolling();

      savePurchasedPlan(plan);

      setStripePurchase((prev) => ({
        ...prev,
        error: '',
        success: true,
      }));

      if (canLogEvents()) {
        sendAnalitycsAfterSubscription(
          plan,
          selectedCoinPlan,
          paymentIntent.id,
        );
      }
    }
    setStripePurchase((prev) => ({ ...prev, isSubmitting: false }));
  };

  const planConfig = webSubscriptionsModule.getPlanConfig(plan);

  const paymentMethodOrder = [
    'card',
    'us_bank_account',
    'google_pay',
    'cashapp',
    'apple_pay',
    'samsung_pay',
    'paypal',
  ];

  return (
    <StripeCardFormStyles onSubmit={handleSubmit} id="stripe-card-form">
      <PaymentElement
        className="payment-element"
        onFocus={handlePaymentElementFocus}
        options={{
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
          paymentMethodOrder,
          layout: 'tabs',
          applePay: {
            recurringPaymentRequest: {
              managementURL: billing_portal_link,
              paymentDescription: plan.label,
              regularBilling: {
                amount: Math.round(plan.price * 100),
                label: planConfig.display,
                recurringPaymentIntervalCount:
                  planConfig.recurringPaymentIntervalCount,
                recurringPaymentIntervalUnit:
                  planConfig.recurringPaymentIntervalUnit,
              },
            },
          },
        }}
      />
      {stripePurchase.error && (
        <MessageStyles color="danger">{stripePurchase.error}</MessageStyles>
      )}
      {stripePurchase.success && (
        <MessageStyles>
          We are processing your payment...
          <IonSpinner name="circles" />
        </MessageStyles>
      )}
      <ButtonStyles
        type="submit"
        disabled={stripePurchase.isSubmitting || disablePurchase || disabled}
        theme={theme}
        className="stripe-form-button"
      >
        {stripePurchase.isSubmitting ? (
          <IonSpinner name="circles" />
        ) : (
          <IonIcon icon={lockClosed} />
        )}
        {buttonText}
      </ButtonStyles>
    </StripeCardFormStyles>
  );
};
